import React, { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { collectionPieces } from '../utils/mockData';
import { useContent, resolveAsset } from '../contexts/ContentContext';
import ProductGrid from '../components/ProductGrid';
import InquiryForm from '../components/InquiryForm';
import SEO from '../components/SEO';
import CommissionDisclaimer from '../components/CommissionDisclaimer';

const mapDb = (p) => ({
  id: p.id,
  type: p.type,
  name: p.name,
  description: p.description,
  technique: p.embroidery || 'Bespoke',
  material: p.fabric || 'Pure Silk',
  hours: 200,
  image: resolveAsset(p.image),
  _search: [p.name, p.description, p.fabric, p.embroidery, p.origin]
    .filter(Boolean).join(' ').toLowerCase(),
});

const TYPE_TABS = [
  { id: 'all', label: 'All Pieces' },
  { id: 'archive', label: 'Heritage Couture' },
  { id: 'suit', label: 'Bespoke Suits' },
];

const ArchivePage = () => {
  const { products } = useContent();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  const q = (searchParams.get('q') || '').trim().toLowerCase();
  const typeParam = (searchParams.get('type') || 'all').toLowerCase();
  const activeType = TYPE_TABS.some((t) => t.id === typeParam) ? typeParam : 'all';

  const allItems = useMemo(() => {
    const dbItems = products.filter((p) => p.type === 'archive' || p.type === 'suit').map(mapDb);
    return dbItems.length ? dbItems : collectionPieces;
  }, [products]);

  const items = useMemo(() => {
    let list = allItems;
    if (activeType !== 'all') list = list.filter((it) => it.type === activeType);
    if (q) {
      list = list.filter((it) => {
        const hay = it._search || `${it.name} ${it.description} ${it.material} ${it.technique}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [allItems, activeType, q]);

  const setType = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'all') next.delete('type');
    else next.set('type', id);
    setSearchParams(next);
  };

  const clearFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    setSearchParams(next);
  };

  const handleInquire = (piece) => {
    setSelectedPiece(piece);
    setInquiryOpen(true);
  };

  const seoTitle =
    activeType === 'suit' ? 'Bespoke Suits — Collection'
    : activeType === 'archive' ? 'Heritage Couture — Collection'
    : q ? `Collection \u2014 ${q.charAt(0).toUpperCase() + q.slice(1)}`
    : 'The Collection';

  return (
    <section style={styles.page} data-testid="archive-page">
      <SEO
        title={seoTitle}
        description="The complete anthology of Silk & Sentiment heritage couture and bespoke suits \u2014 hand-embroidered bridal lehengas, ghararas, anarkalis, sharara sets and sarees crafted over hundreds of hours."
        path={activeType !== 'all' ? `/archive?type=${activeType}` : '/archive'}
      />
      <div className="luxury-container">
        <Link to="/" style={styles.back} data-testid="archive-back">
          <ArrowLeft size={18} />
          <span>Back to Home</span>
        </Link>

        <header style={styles.header}>
          <p style={styles.eyebrow}>The Collection</p>
          <h1 className="heading-1" style={styles.title}>Timeless Creations</h1>
          <p style={styles.lead}>
            The complete anthology of heritage pieces and bespoke suits — each crafted over
            hundreds of hours, each one an heirloom in the making.
          </p>

          <div className="archive-tabs" role="tablist" aria-label="Filter by category">
            {TYPE_TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeType === t.id}
                className={`archive-tab ${activeType === t.id ? 'is-active' : ''}`}
                onClick={() => setType(t.id)}
                data-testid={`archive-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {q && (
            <div style={styles.filterPill} data-testid="archive-filter-pill">
              <span style={styles.filterLabel}>
                Showing pieces featuring{' '}
                <strong style={{ color: 'var(--accent-gold)', textTransform: 'capitalize' }}>{q}</strong>
                {items.length > 0 && (
                  <span style={{ color: 'var(--text-light)', fontWeight: 400, marginLeft: 8 }}>
                    · {items.length} {items.length === 1 ? 'piece' : 'pieces'}
                  </span>
                )}
              </span>
              <button
                onClick={clearFilter}
                style={styles.filterClear}
                aria-label="Clear filter"
                data-testid="archive-filter-clear"
              >
                <X size={12} /> Clear
              </button>
            </div>
          )}
        </header>

        {items.length === 0 ? (
          <div style={styles.empty} data-testid="archive-empty">
            <p className="body-large" style={{ marginBottom: 24 }}>
              {q ? (
                <>No pieces match <strong style={{ color: 'var(--accent-gold)' }}>{q}</strong> just yet.</>
              ) : (
                <>New pieces in this category are arriving soon.</>
              )}
            </p>
            {q ? (
              <button onClick={clearFilter} className="btn-secondary">View Full Collection</button>
            ) : (
              <button onClick={() => setType('all')} className="btn-secondary">View Full Collection</button>
            )}
          </div>
        ) : (
          <>
            <ProductGrid
              items={items}
              variant="collection"
              onInquire={handleInquire}
              testIdPrefix="archive"
            />
            <CommissionDisclaimer />
          </>
        )}

        <InquiryForm
          isOpen={inquiryOpen}
          onClose={() => setInquiryOpen(false)}
          selectedPiece={selectedPiece}
        />
      </div>
    </section>
  );
};

const styles = {
  page: {
    padding: '140px 0 100px',
    backgroundColor: 'var(--bg-primary)',
    minHeight: '100vh',
  },
  back: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    textDecoration: 'none',
    fontFamily: "'Inter', sans-serif",
    fontSize: '12px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'var(--text-secondary)',
    marginBottom: '40px',
    transition: 'color 0.3s ease',
  },
  header: {
    textAlign: 'center',
    maxWidth: '720px',
    margin: '0 auto 64px',
  },
  eyebrow: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: 'var(--accent-gold)',
    marginBottom: '16px',
  },
  title: {
    marginBottom: '20px',
  },
  lead: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '16px',
    lineHeight: '1.8',
    color: 'var(--text-secondary)',
  },
  filterPill: {
    marginTop: '24px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '14px',
    padding: '10px 18px',
    background: 'rgba(201,169,97,0.08)',
    border: '1px solid rgba(201,169,97,0.3)',
    borderRadius: '999px',
  },
  filterLabel: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '12px',
    letterSpacing: '0.06em',
    color: 'var(--text-secondary)',
    fontWeight: 500,
  },
  filterClear: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '999px',
    transition: 'all 0.2s ease',
  },
  empty: {
    textAlign: 'center',
    padding: '80px 0',
  },
};

// Scoped tab styles (loaded once)
const TAB_STYLE_ID = 'archive-tabs-styles';
if (typeof document !== 'undefined' && !document.getElementById(TAB_STYLE_ID)) {
  const css = document.createElement('style');
  css.id = TAB_STYLE_ID;
  css.textContent = `
    .archive-tabs {
      margin-top: 36px;
      display: inline-flex;
      gap: 8px;
      flex-wrap: wrap;
      justify-content: center;
      padding: 6px;
      background: rgba(201,169,97,0.06);
      border: 1px solid rgba(201,169,97,0.18);
      border-radius: 999px;
    }
    .archive-tab {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 10px 22px;
      font-family: 'Inter', sans-serif;
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--text-secondary);
      border-radius: 999px;
      transition: all 0.3s cubic-bezier(.2,.8,.2,1);
    }
    .archive-tab:hover { color: var(--text-primary); }
    .archive-tab.is-active {
      background: var(--text-primary);
      color: var(--bg-primary);
      box-shadow: 0 1px 8px rgba(43,43,43,0.18);
    }
    @media (max-width: 640px) {
      .archive-tabs { gap: 4px; padding: 4px; }
      .archive-tab { padding: 9px 14px; font-size: 10px; letter-spacing: 0.14em; }
    }
  `;
  document.head.appendChild(css);
}

export default ArchivePage;
